import { useEffect, useState } from "react";
import { Crown, Award } from "lucide-react";
import api from "@/lib/api";
import Medal from "@/components/Medal";

const MEDAL_COLOR = { 1: "#D4AF37", 2: "#C0C0C0", 3: "#CD7F32" };

const prefersReduced =
  typeof window !== "undefined" && window.matchMedia
    ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
    : false;

/* Small circular avatar for #4–#10 — renders nothing when missing or failed to load. */
function RowAvatar({ entry, size, variant = "" }) {
  const [failed, setFailed] = useState(false);
  if (!entry.avatar_url || failed) return null;
  return (
    <img
      src={entry.avatar_url}
      alt={entry.name}
      onError={() => setFailed(true)}
      data-testid={`top-avatar-${variant}${entry.position}`}
      className="rounded-full object-cover border border-[#D4AF37]/40 shrink-0"
      style={{ width: size, height: size }}
    />
  );
}

/* ---------- Top 3 medal podium card (desktop + mobile; sizes via props) ---------- */
function PodiumCard({ entry, medal, compact }) {
  const color = MEDAL_COLOR[entry.position];
  const first = entry.position === 1;
  return (
    <div
      className={`rounded-2xl bg-gradient-to-b from-[#15161c] to-[#07080a] border text-center min-w-0 flex flex-col ${
        first ? "border-[#D4AF37]/50 shadow-[0_0_28px_rgba(212,175,55,0.14)]" : "border-[#2a2d38]"
      } ${compact ? (first ? "px-1.5 pt-2 pb-2 -mt-7" : "px-1.5 pt-2 pb-2") : first ? "px-4 pt-4 pb-4 -mt-8" : "px-4 pt-4 pb-4"}`}
      data-testid={`top-member-${compact ? "mobile-" : ""}${entry.position}`}
    >
      <div className={compact ? "h-4" : "h-7"}>
        {first && <Crown className={`${compact ? "h-4 w-4" : "h-6 w-6"} mx-auto`} style={{ color }} />}
      </div>
      <div className="flex justify-center">
        <Medal position={entry.position} avatarUrl={entry.avatar_url} alt={entry.name} size={medal} testId={`top-medal-${compact ? "mobile-" : ""}${entry.position}`} />
      </div>
      <div className={`mt-2 rounded-lg bg-black/70 border border-[#222530] ${compact ? "px-1 py-1.5" : "px-3 py-2.5"}`}>
        <p className={`font-mono font-bold leading-none ${compact ? "text-[11px]" : "text-sm"}`} style={{ color }}>#{entry.position}</p>
        <p className={`text-white font-semibold leading-tight mt-1 break-words ${compact ? "text-[11px]" : "text-sm"}`}>{entry.name}</p>
        <p className={`font-mono tracking-widest uppercase mt-1 break-words leading-tight ${compact ? "text-[7px]" : "text-[10px]"}`} style={{ color }}>{entry.rank_name}</p>
      </div>
    </div>
  );
}

/* ---------- Shared MY RANK card (mobile + desktop) ---------- */
function MyRankCard({ position }) {
  const ranked = typeof position === "number" && position > 0;
  return (
    <div
      className="card-lux rounded-2xl border border-[#D4AF37]/40 shadow-[0_0_30px_rgba(212,175,55,0.12)] flex items-stretch overflow-hidden"
      data-testid="my-rank-card"
    >
      <div className="flex-1 min-w-0 flex flex-col justify-center px-5 py-4">
        <p className="text-white font-semibold tracking-wide text-sm uppercase">My Rank</p>
        <p className="text-[10px] font-mono tracking-widest uppercase text-[#64748B] mt-0.5 truncate">Across PLUSMAKER</p>
      </div>
      <div className="w-px bg-[#D4AF37]/25 my-3" />
      <div className="flex-1 flex items-center justify-center px-5 py-4">
        {ranked ? (
          <p className="font-mono font-black text-[#D4AF37] text-4xl leading-none" data-testid="my-rank-value">#{position}</p>
        ) : (
          <p className="font-mono font-semibold text-[#64748B] text-lg" data-testid="my-rank-value">Not ranked</p>
        )}
      </div>
    </div>
  );
}

export default function TopMembers() {
  const [items, setItems] = useState(null);

  useEffect(() => {
    api.get("/member/top-members").then((res) => setItems(res.data)).catch(() => setItems([]));
  }, []);

  const podium = (items || []).slice(0, 3);
  const rest = (items || []).slice(3);
  const podiumOrder = [podium[1], podium[0], podium[2]].filter(Boolean);
  const myPosition = items && items.length ? items[0].my_position : null;

  return (
    <div className="max-w-4xl mx-auto space-y-5" data-testid="top-members-page">
      <div className="text-center">
        <p className="eyebrow-label">Elite Circle</p>
        <div className="flex items-center justify-center gap-3 mt-2">
          <h1 className="page-title">Top 10 Members</h1>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-[#D4AF37]/40 px-2.5 py-1" data-testid="top-live-badge" title="Current ecosystem standings">
            <span className={`h-2 w-2 rounded-full bg-[#D4AF37] ${prefersReduced ? "" : "animate-pulse"}`} />
            <span className="text-[10px] font-mono tracking-widest uppercase text-[#D4AF37]">Live</span>
          </span>
        </div>
        <p className="page-sub mt-1">Current standings within the PLUSMAKER ecosystem.</p>
      </div>

      {items === null ? (
        <p className="text-center text-[#64748B] text-sm">Loading…</p>
      ) : items.length === 0 ? (
        <div className="card-lux p-10 text-center" data-testid="top-members-empty">
          <Crown className="h-8 w-8 text-[#D4AF37]/40 mx-auto mb-3" />
          <p className="text-[#64748B] text-sm">The leaderboard will appear as members climb the ranks.</p>
        </div>
      ) : (
        <>
          {/* Top 3 — medal podium: #2 silver left, #1 gold centre (taller, crown), #3 bronze right */}
          <div className="hidden sm:grid grid-cols-3 gap-5 items-start pt-8" data-testid="top-members-podium">
            {podiumOrder.map((entry) => (
              <PodiumCard key={entry.position} entry={entry} medal={entry.position === 1 ? 128 : 104} />
            ))}
          </div>
          <div className="sm:hidden grid grid-cols-3 gap-2 items-start pt-7" data-testid="top-members-podium-mobile">
            {podiumOrder.map((entry) => (
              <PodiumCard key={entry.position} entry={entry} medal={entry.position === 1 ? 86 : 72} compact />
            ))}
          </div>

          {/* Monthly recognition — compact announcement (not confirmed winners) */}
          <div className="card-lux px-4 py-3 border border-[#D4AF37]/25 flex items-stretch gap-3" data-testid="top-recognition">
            <div className="shrink-0 self-center h-10 w-10 rounded-lg bg-[#D4AF37]/10 border border-[#D4AF37]/40 flex items-center justify-center">
              <Award className="h-4 w-4 text-[#D4AF37]" />
            </div>
            <div className="w-px bg-[#222530]" />
            <div className="flex-1 min-w-0">
              <h3 className="text-white font-semibold text-sm leading-tight">Monthly Top 3 Recognition</h3>
              <p className="text-xs text-[#94A3B8] leading-snug mt-1">
                PLUSMAKER will award personalised plaques to the Top 3 clients each month, engraved with their name, rank and month.
              </p>
              <p className="text-[10px] font-mono tracking-wide uppercase text-[#64748B] mt-1.5">Current standings are provisional</p>
            </div>
          </div>

          {/* #4–#10 */}
          {rest.length > 0 && (
            <div className="card-lux rounded-2xl divide-y divide-[#222530] overflow-hidden" data-testid="top-members-list">
              {rest.map((entry) => (
                <div key={entry.position} className="flex items-center gap-3 sm:gap-4 px-4 sm:px-5 py-3 min-w-0" data-testid={`leaderboard-row-${entry.position}`}>
                  <span className="w-7 shrink-0 text-center font-mono font-bold text-base text-[#D4AF37]">{entry.position}</span>
                  <RowAvatar entry={entry} size={34} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm sm:text-base text-white font-medium truncate">{entry.name}</p>
                    <p className="text-[10px] font-mono tracking-widest uppercase text-[#D4AF37]/85 truncate mt-0.5">{entry.rank_name}</p>
                  </div>
                  {entry.rank_image_url && <img loading="lazy" src={entry.rank_image_url} alt="" className="h-7 sm:h-8 shrink-0 object-contain" />}
                </div>
              ))}
            </div>
          )}

          {/* MY RANK — appended after the full Top 10 */}
          <MyRankCard position={myPosition} />
        </>
      )}
    </div>
  );
}
